import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useQueryClient } from '@tanstack/react-query';
import {
  getGetAllowedOrganizationOfferGroupsQueryKey,
  getGetAllowedOrganizationTagsQueryKey,
  getGetOrganizationByIDQueryKey,
  useListTenantCollectionTicketClaims,
  useSetAllowedOrganizationOfferGroups,
  useSetAllowedOrganizationTags,
  useUpdateOrganizationByID,
} from '@/api/trafikportalen';
import { toast } from '@/hooks/useToast';
import { Section } from '../Section';
import { WarningDialog } from '../WarningDialog';
import CheckBox from '../core/Checkbox';
import Input from '../core/Input';
import Button from '../core/Button';
import { IconCheck, IconChevronRight, IconWarningTriangle } from '@/icons';
import { cn } from '@/helpers/classnames';
import { hasPeriodActiveThisOrPreviousMonth } from '@/helpers/periodEndDate';
import { offerGroupIdsUsedByActiveOrUpcomingPeriods } from '@/helpers/staticTicket';
import { blankToUndefined } from '@/helpers/trimData';
import { CheckboxSection } from './CheckboxSection';
import { StudentCategoryPeriodList } from './StudentCategoryPeriodList';
import type { OrganizationDTO } from '@/api/trafikportalen';
import type { CollectionTypeOptInDTO } from '@/api/trafikportalen/models/collectionTypeOptInDTO';
import { useAutoAnimateConditional } from '@/hooks/useAutoAnimateConditional';
import { WarningBanner } from '../core/WarningBanner';

export interface CheckboxItem {
  id: string;
  title: string;
  subtitle?: string;
}

export interface OfferGroupCheckboxItem extends CheckboxItem {
  groupKey: string;
}

export interface StudentCategorySettingsProps {
  organization: OrganizationDTO;
  collectionType: CollectionTypeOptInDTO;
  title: string;
  description: React.ReactNode;
  enabledTypes: CollectionTypeOptInDTO[];
  onSaved?: () => void;
  offerGroupItems: OfferGroupCheckboxItem[];
  allowedOfferGroupIds: string[];
  isLoadingOfferGroups: boolean;
  tagItems: CheckboxItem[];
  allowedTagIds: string[];
  isLoadingTags: boolean;
  disableHalfProduct?: boolean;
  disableDiscountCodes?: boolean;
}

export const StudentCategorySettings = ({
  organization,
  collectionType,
  title,
  description,
  enabledTypes,
  onSaved,
  offerGroupItems,
  allowedOfferGroupIds,
  isLoadingOfferGroups,
  tagItems,
  allowedTagIds,
  isLoadingTags,
  disableHalfProduct = false,
  disableDiscountCodes = false,
}: StudentCategorySettingsProps) => {
  const [ref] = useAutoAnimateConditional<HTMLDivElement>();
  const tagLockMessageId = useId();
  const offerGroupLockMessageId = useId();
  const { formatMessage } = useIntl();
  const queryClient = useQueryClient();
  const updateOrganization = useUpdateOrganizationByID();
  const setAllowedOfferGroups = useSetAllowedOrganizationOfferGroups();
  const setAllowedTags = useSetAllowedOrganizationTags();

  const isEnabled = enabledTypes.includes(collectionType);
  const showTicketSettings = organization.selfmanaged_school_claims === true;
  const disableDynamicTickets = collectionType === 'school-child-and-youth';
  const [collapsed, setCollapsed] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [checked, setChecked] = useState(isEnabled);
  const [showWarning, setShowWarning] = useState(false);
  const [selectedOfferGroups, setSelectedOfferGroups] =
    useState<string[]>(allowedOfferGroupIds);
  const [selectedTags, setSelectedTags] = useState<string[]>(allowedTagIds);

  const savedDiscountCodes = organization.discount_codes?.[collectionType];
  const [discountCodeHalf, setDiscountCodeHalf] = useState(
    savedDiscountCodes?.half_product ?? '',
  );
  const [discountCodeFull, setDiscountCodeFull] = useState(
    savedDiscountCodes?.full_product ?? '',
  );

  useEffect(() => {
    if (editMode) return;
    setChecked(isEnabled);
    setSelectedOfferGroups(allowedOfferGroupIds);
    setSelectedTags(allowedTagIds);
    setDiscountCodeHalf(savedDiscountCodes?.half_product ?? '');
    setDiscountCodeFull(savedDiscountCodes?.full_product ?? '');
  }, [
    isEnabled,
    allowedOfferGroupIds,
    allowedTagIds,
    savedDiscountCodes,
    editMode,
  ]);

  const claimsNeeded = !collapsed && (isEnabled || editMode);
  const claimParams = useMemo(
    () => ({
      collection_type: [collectionType],
      organization_ids: [organization.id],
    }),
    [collectionType, organization.id],
  );
  const {
    data: claims,
    isLoading: isLoadingClaims,
    isError: isClaimsError,
  } = useListTenantCollectionTicketClaims(claimParams, {
    query: { enabled: claimsNeeded },
  });
  const hasRecentPeriod = useMemo(
    () => hasPeriodActiveThisOrPreviousMonth(claims ?? []),
    [claims],
  );
  const tagRemovalLocked =
    claimsNeeded &&
    !disableDynamicTickets &&
    (isLoadingClaims || isClaimsError || hasRecentPeriod);

  const lockedOfferGroupIds = useMemo(() => {
    if (!claimsNeeded || allowedOfferGroupIds.length === 0) return [];
    if (isLoadingClaims || isClaimsError) return allowedOfferGroupIds;
    const used = new Set(
      offerGroupIdsUsedByActiveOrUpcomingPeriods(offerGroupItems, claims ?? []),
    );
    return allowedOfferGroupIds.filter((id) => used.has(id));
  }, [
    allowedOfferGroupIds,
    claims,
    claimsNeeded,
    isClaimsError,
    isLoadingClaims,
    offerGroupItems,
  ]);
  const offerGroupRemovalLocked = lockedOfferGroupIds.length > 0;

  const isDisabling = isEnabled && !checked;
  const isPending =
    updateOrganization.isPending ||
    setAllowedOfferGroups.isPending ||
    setAllowedTags.isPending;
  const showDetails = editMode ? checked : isEnabled;

  const offerGroupsChanged = useMemo(() => {
    if (selectedOfferGroups.length !== allowedOfferGroupIds.length) return true;
    return selectedOfferGroups.some((id) => !allowedOfferGroupIds.includes(id));
  }, [selectedOfferGroups, allowedOfferGroupIds]);

  const tagsChanged = useMemo(() => {
    if (disableDynamicTickets) return false;
    if (selectedTags.length !== allowedTagIds.length) return true;
    return selectedTags.some((id) => !allowedTagIds.includes(id));
  }, [disableDynamicTickets, selectedTags, allowedTagIds]);

  const discountCodesChanged =
    !disableDiscountCodes &&
    (discountCodeFull !== (savedDiscountCodes?.full_product ?? '') ||
      (!disableHalfProduct &&
        discountCodeHalf !== (savedDiscountCodes?.half_product ?? '')));

  const hasChanges =
    checked !== isEnabled ||
    offerGroupsChanged ||
    tagsChanged ||
    discountCodesChanged;

  const startEdit = useCallback(() => {
    setChecked(isEnabled);
    setSelectedOfferGroups(allowedOfferGroupIds);
    setSelectedTags(allowedTagIds);
    setDiscountCodeHalf(savedDiscountCodes?.half_product ?? '');
    setDiscountCodeFull(savedDiscountCodes?.full_product ?? '');
    setCollapsed(false);
    setEditMode(true);
  }, [isEnabled, allowedOfferGroupIds, allowedTagIds, savedDiscountCodes]);

  const cancelEdit = useCallback(() => {
    setChecked(isEnabled);
    setSelectedOfferGroups(allowedOfferGroupIds);
    setSelectedTags(allowedTagIds);
    setDiscountCodeHalf(savedDiscountCodes?.half_product ?? '');
    setDiscountCodeFull(savedDiscountCodes?.full_product ?? '');
    setEditMode(false);
  }, [isEnabled, allowedOfferGroupIds, allowedTagIds, savedDiscountCodes]);

  const performSave = useCallback(async () => {
    const newEnabledTypes = checked
      ? [...enabledTypes.filter((t) => t !== collectionType), collectionType]
      : enabledTypes.filter((t) => t !== collectionType);

    try {
      await updateOrganization.mutateAsync({
        organizationID: organization.id,
        data: {
          name: organization.name,
          organization_number: organization.organization_number,
          state: organization.state,
          credit: organization.credit,
          discount_codes: disableDiscountCodes
            ? (organization.discount_codes ?? {})
            : {
                ...(organization.discount_codes ?? {}),
                [collectionType]: disableHalfProduct
                  ? {
                      full_product: blankToUndefined(discountCodeFull),
                    }
                  : {
                      half_product: blankToUndefined(discountCodeHalf),
                      full_product: blankToUndefined(discountCodeFull),
                    },
              },
          enabled_collection_types: newEnabledTypes,
        },
      });

      if (checked && showTicketSettings) {
        await setAllowedOfferGroups.mutateAsync({
          organizationID: organization.id,
          data: {
            collection_type: collectionType,
            offers: [
              ...new Set([...lockedOfferGroupIds, ...selectedOfferGroups]),
            ],
          },
        });

        if (!disableDynamicTickets) {
          const tagsToSave = tagRemovalLocked
            ? [...new Set([...allowedTagIds, ...selectedTags])]
            : selectedTags;
          await setAllowedTags.mutateAsync({
            organizationID: organization.id,
            data: {
              collection_type: collectionType,
              tags: tagsToSave,
            },
          });
        }
      }

      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: getGetOrganizationByIDQueryKey(organization.id),
        }),
        queryClient.invalidateQueries({
          queryKey: getGetAllowedOrganizationOfferGroupsQueryKey(
            organization.id,
          ),
        }),
        queryClient.invalidateQueries({
          queryKey: getGetAllowedOrganizationTagsQueryKey(organization.id),
        }),
      ]);

      setEditMode(false);
      setShowWarning(false);
      onSaved?.();

      toast.success(
        {
          title: formatMessage({
            defaultMessage: 'Inställningarna sparades',
          }),
        },
        { autoClose: 3000 },
      );
    } catch {
      toast.error(
        {
          title: formatMessage({
            defaultMessage: 'Kunde inte spara inställningarna',
          }),
        },
        { autoClose: 3000 },
      );
    }
  }, [
    checked,
    collectionType,
    discountCodeFull,
    discountCodeHalf,
    enabledTypes,
    formatMessage,
    onSaved,
    organization,
    queryClient,
    allowedTagIds,
    disableDynamicTickets,
    lockedOfferGroupIds,
    selectedOfferGroups,
    selectedTags,
    showTicketSettings,
    tagRemovalLocked,
    setAllowedOfferGroups,
    setAllowedTags,
    updateOrganization,
    disableHalfProduct,
    disableDiscountCodes,
  ]);

  const handleSave = useCallback(() => {
    if (isDisabling) {
      setShowWarning(true);
      return;
    }
    performSave();
  }, [isDisabling, performSave]);

  const summaryParts = useMemo(() => {
    const parts: string[] = [];
    if (!showTicketSettings) return parts;
    if (offerGroupItems.length > 0) {
      parts.push(
        formatMessage(
          {
            defaultMessage:
              '{count}/{total, plural, one {# biljett} other {# biljetter}}',
          },
          {
            count: allowedOfferGroupIds.length,
            total: offerGroupItems.length,
          },
        ),
      );
    }
    if (!disableDynamicTickets && tagItems.length > 0) {
      parts.push(
        formatMessage(
          {
            defaultMessage:
              '{count}/{total, plural, one {# biljettkategori} other {# biljettkategorier}}',
          },
          {
            count: allowedTagIds.length,
            total: tagItems.length,
          },
        ),
      );
    }
    return parts;
  }, [
    allowedOfferGroupIds.length,
    allowedTagIds.length,
    disableDynamicTickets,
    formatMessage,
    offerGroupItems.length,
    showTicketSettings,
    tagItems.length,
  ]);

  const handleOfferGroupsChange = useCallback(
    (ids: string[]) => {
      setSelectedOfferGroups([...new Set([...lockedOfferGroupIds, ...ids])]);
    },
    [lockedOfferGroupIds],
  );

  const handleTagsChange = useCallback(
    (ids: string[]) => {
      if (!tagRemovalLocked) {
        setSelectedTags(ids);
        return;
      }
      setSelectedTags([...new Set([...allowedTagIds, ...ids])]);
    },
    [allowedTagIds, tagRemovalLocked],
  );

  const lockedTagTooltip = isLoadingClaims
    ? formatMessage({
        defaultMessage: 'Kontrollerar om biljettkategorier kan tas bort.',
      })
    : isClaimsError
      ? formatMessage({
          defaultMessage:
            'Perioderna kunde inte hämtas, så den här biljettkategorin kan inte tas bort.',
        })
      : formatMessage({
          defaultMessage:
            'Kan inte tas bort när en period varit aktiv den här eller föregående månaden.',
        });

  const lockedOfferGroupTooltip = isLoadingClaims
    ? formatMessage({
        defaultMessage: 'Kontrollerar om statiska biljetter kan tas bort.',
      })
    : isClaimsError
      ? formatMessage({
          defaultMessage:
            'Perioderna kunde inte hämtas, så den här biljetten kan inte tas bort.',
        })
      : formatMessage({
          defaultMessage:
            'Kan inte tas bort när en aktiv eller kommande period använder den här biljetten.',
        });

  const toggleCollapsed = useCallback(() => {
    if (!editMode) {
      setCollapsed((prev) => !prev);
    }
  }, [editMode]);

  const editButtons = (
    <div className="flex shrink-0 justify-end gap-2">
      <Button variant="ghost" onClick={cancelEdit} disabled={isPending}>
        <FormattedMessage defaultMessage="Avbryt" />
      </Button>
      <Button
        variant="filled"
        onClick={handleSave}
        loading={isPending}
        disabled={!hasChanges}
        startIcon={<IconCheck />}
      >
        <FormattedMessage defaultMessage="Spara" />
      </Button>
    </div>
  );

  return (
    <Section>
      <div ref={ref} className="flex w-full flex-col gap-4">
        <div className="flex items-start justify-between gap-4">
          <button
            type="button"
            className="flex flex-1 cursor-pointer items-start gap-2 text-left"
            onClick={toggleCollapsed}
            aria-expanded={!collapsed}
          >
            <span
              className={cn(
                'mt-1 shrink-0 transition-transform',
                !collapsed && 'rotate-90',
              )}
              aria-hidden="true"
            >
              <IconChevronRight className="size-5" />
            </span>
            <div className="flex flex-col gap-1">
              <h2>{title}</h2>
              {collapsed ? (
                <p className="text-sm text-text-700">
                  {isEnabled ? (
                    <>
                      <span className="rounded bg-primary-500 px-1.5 py-0.5 font-medium text-white">
                        <FormattedMessage defaultMessage="Aktiverad" />
                      </span>
                      {summaryParts.length > 0 &&
                        ` — ${summaryParts.join(', ')}`}
                    </>
                  ) : (
                    <FormattedMessage defaultMessage="Inaktiverad" />
                  )}
                </p>
              ) : (
                <p className="text-text-700">{description}</p>
              )}
            </div>
          </button>
          {editMode && editButtons}
          {!editMode && !collapsed && (
            <Button variant="ghost" onClick={startEdit} className="shrink-0">
              <FormattedMessage defaultMessage="Redigera" />
            </Button>
          )}
        </div>

        {!collapsed && (
          <>
            <CheckBox
              id={`checkbox-${collectionType}`}
              checked={editMode ? checked : isEnabled}
              onClick={() => setChecked((prev) => !prev)}
              disabled={!editMode}
              label={formatMessage({ defaultMessage: 'Aktiverad' })}
            />

            {isEnabled && (
              <StudentCategoryPeriodList
                organizationId={organization.id}
                collectionType={collectionType}
              />
            )}

            {showDetails && !disableDiscountCodes && (
              <div className="mt-2 flex flex-col gap-2">
                <h3>
                  <FormattedMessage defaultMessage="Rabattkoder" />
                </h3>
                <p className="text-text-700">
                  <FormattedMessage defaultMessage="Rabattkod från Bobcat. Appliceras automatiskt vid köp av biljetter i denna kategori. Gäller inte för manuellt köpta biljetter." />
                </p>
                <div className="mt-5 flex flex-col gap-7 sm:flex-row">
                  <Input
                    id={`${collectionType}-discount-full`}
                    label={formatMessage({
                      defaultMessage: 'Rabattkod',
                    })}
                    type="text"
                    value={
                      editMode
                        ? discountCodeFull
                        : (savedDiscountCodes?.full_product ?? '')
                    }
                    onChange={(e) => setDiscountCodeFull(e.target.value)}
                    disabled={!editMode}
                  />
                  {!disableHalfProduct && (
                    <Input
                      id={`${collectionType}-discount-half`}
                      label={formatMessage({
                        defaultMessage: 'Halv produkt',
                      })}
                      type="text"
                      value={
                        editMode
                          ? discountCodeHalf
                          : (savedDiscountCodes?.half_product ?? '')
                      }
                      onChange={(e) => setDiscountCodeHalf(e.target.value)}
                      disabled={!editMode}
                      helperText={formatMessage({
                        defaultMessage:
                          'Appliceras när "halv produkt" är satt, istället för "Rabattkod".',
                      })}
                      helperTextReserveSpace
                    />
                  )}
                </div>
              </div>
            )}

            {showDetails &&
              showTicketSettings &&
              offerGroupItems.length > 0 && (
                <CheckboxSection
                  title={
                    <FormattedMessage defaultMessage="Statiska biljetter" />
                  }
                  description={
                    <div className="flex flex-col gap-2">
                      <p>
                        <FormattedMessage defaultMessage="Välj vilka statiska biljetter som ska vara tillgängliga för denna elevtyp." />
                      </p>
                      {offerGroupRemovalLocked &&
                        (isLoadingClaims ? (
                          <span id={offerGroupLockMessageId} role="status">
                            <FormattedMessage defaultMessage="Kontrollerar om statiska biljetter kan tas bort." />
                          </span>
                        ) : (
                          <WarningBanner
                            id={offerGroupLockMessageId}
                            icon={<IconWarningTriangle />}
                          >
                            {isClaimsError ? (
                              <FormattedMessage defaultMessage="Perioderna kunde inte hämtas, så valda statiska biljetter kan inte tas bort." />
                            ) : (
                              <FormattedMessage defaultMessage="Statiska biljetter som används av en aktiv eller kommande period kan inte tas bort. Nya biljetter kan läggas till." />
                            )}
                          </WarningBanner>
                        ))}
                    </div>
                  }
                  items={offerGroupItems}
                  selectedIds={
                    editMode ? selectedOfferGroups : allowedOfferGroupIds
                  }
                  onSelectionChange={handleOfferGroupsChange}
                  lockedIds={
                    editMode && offerGroupRemovalLocked
                      ? lockedOfferGroupIds
                      : undefined
                  }
                  lockedTooltip={lockedOfferGroupTooltip}
                  lockedDescribedBy={
                    editMode && offerGroupRemovalLocked
                      ? offerGroupLockMessageId
                      : undefined
                  }
                  editMode={editMode}
                  onClickButton={startEdit}
                  canSeeButton={false}
                  isLoading={isLoadingOfferGroups}
                />
              )}

            {showDetails &&
              showTicketSettings &&
              !disableDynamicTickets &&
              tagItems.length > 0 && (
                <CheckboxSection
                  title={
                    <FormattedMessage defaultMessage="Biljettkategorier" />
                  }
                  description={
                    <div className="flex flex-col gap-2">
                      <p>
                        <FormattedMessage defaultMessage="Välj vilka biljettkategorier som ska vara tillgängliga för denna elevtyp." />
                      </p>
                      {tagRemovalLocked &&
                        (isLoadingClaims ? (
                          <span id={tagLockMessageId} role="status">
                            <FormattedMessage defaultMessage="Kontrollerar om biljettkategorier kan tas bort." />
                          </span>
                        ) : (
                          <WarningBanner
                            id={tagLockMessageId}
                            icon={<IconWarningTriangle />}
                          >
                            {isClaimsError ? (
                              <FormattedMessage defaultMessage="Perioderna kunde inte hämtas, så valda biljettkategorier kan inte tas bort." />
                            ) : (
                              <FormattedMessage defaultMessage="Valda biljettkategorier kan inte tas bort eftersom en period varit aktiv den här eller föregående månaden. Nya biljettkategorier kan läggas till." />
                            )}
                          </WarningBanner>
                        ))}
                    </div>
                  }
                  items={tagItems}
                  selectedIds={editMode ? selectedTags : allowedTagIds}
                  onSelectionChange={handleTagsChange}
                  lockedIds={
                    editMode && tagRemovalLocked ? allowedTagIds : undefined
                  }
                  lockedTooltip={lockedTagTooltip}
                  lockedDescribedBy={
                    editMode && tagRemovalLocked ? tagLockMessageId : undefined
                  }
                  editMode={editMode}
                  onClickButton={startEdit}
                  canSeeButton={false}
                  isLoading={isLoadingTags}
                />
              )}

            {editMode && editButtons}
          </>
        )}
      </div>

      <WarningDialog
        open={showWarning}
        onOpenChange={setShowWarning}
        title={formatMessage(
          { defaultMessage: 'Inaktivera {type}' },
          { type: title },
        )}
        content={
          <p>
            <FormattedMessage
              defaultMessage="Är du säker på att du vill inaktivera {type}? Alla personer i denna kategori kommer att raderas direkt och få sina biljetter makulerade."
              values={{ type: title }}
            />
          </p>
        }
        onCancel={() => setShowWarning(false)}
        onConfirm={performSave}
        buttonText={formatMessage({ defaultMessage: 'Inaktivera' })}
        loading={isPending}
      />
    </Section>
  );
};
